import { Badge, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { SquareFunctionIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationAttrExplanationStep } from '@/entities/conversations';
import { formatHumanDate } from '@/shared/lib/utils/date';

type Props = {
  /** Шаги разбора в порядке выполнения правила */
  steps: ConversationAttrExplanationStep[];
  /** Момент вычисления значения (ISO); null — не известен */
  computedAt?: string | null;
};

/**
 * Иконка разбора вычисления значения атрибута и всплывающее окно с его шагами.
 * Окно открывается по клику и закрывается явным действием — уход курсора его
 * не закрывает, потому что разбор читают, сверяя с правилом извлечения.
 */
export const AttrExplanationPopover: FC<Props> = ({ steps, computedAt }) => {
  const { t, i18n } = useTranslation(['dialogs']);

  return (
    <Popover>
      <IconButton
        size="icon"
        className="size-5"
        label={t('dialogs:attrExplanation.iconLabel')}
        icon={<SquareFunctionIcon className="size-3.5 text-muted-foreground" />}
        trigger={PopoverTrigger}
      />
      {/*
        Высоту окна ограничивает и уводит остаток в прокрутку сам PopoverContent
        (`max-h` по свободному месту + `overflow-y-auto`). Здесь задаётся только
        ширина и внутренние отступы: снять ограничение высоты (`max-h-none`) или
        завернуть шаги в собственный контейнер с прокруткой нельзя — длинный
        разбор перестанет помещаться на экран.
      */}
      <PopoverContent className="w-96 p-3" align="end">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t('dialogs:attrExplanation.title')}</span>
            {computedAt && (
              <span className="text-xs text-muted-foreground">
                {t('dialogs:attrExplanation.computedAt', {
                  value: formatHumanDate(i18n, computedAt, { withTime: true }),
                })}
              </span>
            )}
          </div>
          <ol className="flex flex-col gap-3">
            {steps.map((step) => (
              <li key={step.step} className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {t('dialogs:attrExplanation.step', { number: step.step })}
                </span>
                <span className="text-xs font-mono wrap-break-word whitespace-pre-wrap">{step.expression}</span>
                <span className="text-sm wrap-break-word whitespace-pre-wrap">{step.result}</span>
                {step.isTruncated && (
                  <Badge variant="secondary" outline className="self-start">
                    {t('dialogs:attrExplanation.truncated')}
                  </Badge>
                )}
              </li>
            ))}
          </ol>
        </div>
      </PopoverContent>
    </Popover>
  );
};
